import React from 'react';

/**
 * Состояние ношения вещи в образе (wear-state, схема ws-v0) — контракт additive
 * к элементам образа; null/отсутствие = нейтральное состояние.
 */
export interface WearState {
    wearStateVersion: string;
    layerRole?: string | null;
    closureFraction?: number | null;
    tucking?: string | null;
    sleeveState?: string | null;
    cuffState?: string | null;
    cinched?: boolean | null;
}

/** Запись справочника /api/refs/category-capabilities. */
export interface CategoryCapability {
    categoryId: string;
    categoryCode: string;
    canOpen: boolean;
    canTuck: boolean;
    canRollSleeves: boolean;
    canCuff: boolean;
    canCinch: boolean;
}

export const WEAR_STATE_VERSION = 'ws-v0';

/** Нейтрально ли состояние (эквивалент отсутствия wearState). */
export function isNeutralWearState(state: WearState | null | undefined): boolean {
    if (!state) return true;
    return (state.closureFraction === null || state.closureFraction === undefined)
        && (!state.tucking || state.tucking === 'none')
        && (!state.sleeveState || state.sleeveState === 'normal')
        && (!state.cuffState || state.cuffState === 'normal')
        && !state.cinched
        && !state.layerRole;
}

/** Нормализация перед отправкой: нейтральное состояние → undefined (не шлём поле). */
export function toWearStatePayload(state: WearState | null | undefined): WearState | undefined {
    if (isNeutralWearState(state)) return undefined;
    return {...state, wearStateVersion: WEAR_STATE_VERSION} as WearState;
}

interface SegmentOption {
    key: string;
    label: string;
    active: boolean;
    onSelect: () => void;
}

interface WearStateControlsProps {
    /** Возможности категории вещи; null/undefined — все переключатели скрыты. */
    capability: CategoryCapability | null | undefined;
    value: WearState | null;
    onChange: (next: WearState | null) => void;
}

const groupLabelClass = 'text-xs text-gray-500 dark:text-gray-400 w-24 flex-shrink-0';

/**
 * Переключатели доступных состояний ношения по capabilities категории (wear-state):
 * сегмент-контролы «застёгнута/нараспашку», «заправлена/навыпуск», «рукава», подвороты и
 * утяжка. Показываются только доступные по справочнику группы; тач-зоны ≥ 44px; повторный
 * клик по активному сегменту возвращает нейтральное состояние. Живой пересчёт скора —
 * часть B фичи; здесь переключатели только формируют состояние для сохранения.
 */
const WearStateControls: React.FC<WearStateControlsProps> = ({capability, value, onChange}) => {
    if (!capability) return null;

    const state: WearState = value ?? {wearStateVersion: WEAR_STATE_VERSION};

    const emit = (patch: Partial<WearState>) => {
        const next: WearState = {...state, ...patch, wearStateVersion: WEAR_STATE_VERSION};
        onChange(isNeutralWearState(next) ? null : next);
    };

    const segment = (options: SegmentOption[]) => (
        <div className="inline-flex rounded-xl border border-gray-200 dark:border-ink-700 overflow-hidden" role="group">
            {options.map(opt => (
                <button
                    key={opt.key}
                    type="button"
                    onClick={opt.onSelect}
                    aria-pressed={opt.active}
                    className={`min-h-[44px] min-w-[44px] px-3 text-xs font-medium transition-colors
                        ${opt.active
                            ? 'bg-brand-500 text-white'
                            : 'bg-white dark:bg-ink-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-ink-800'}`}
                >
                    {opt.label}
                </button>
            ))}
        </div>
    );

    const groups: React.ReactNode[] = [];

    if (capability.canOpen) {
        const closed = state.closureFraction != null && state.closureFraction >= 0.9;
        const open = state.closureFraction != null && state.closureFraction <= 0.1;
        groups.push(
            <div key="closure" className="flex items-center gap-2">
                <span className={groupLabelClass}>Застёжка</span>
                {segment([
                    {key: 'closed', label: 'Застёгнута', active: closed,
                        onSelect: () => emit({closureFraction: closed ? null : 1})},
                    {key: 'open', label: 'Нараспашку', active: open,
                        onSelect: () => emit({closureFraction: open ? null : 0})},
                ])}
            </div>,
        );
    }

    if (capability.canTuck) {
        const tucked = state.tucking === 'full';
        const untucked = state.tucking === 'none';
        groups.push(
            <div key="tucking" className="flex items-center gap-2">
                <span className={groupLabelClass}>Заправка</span>
                {segment([
                    {key: 'tucked', label: 'Заправлена', active: tucked,
                        onSelect: () => emit({tucking: tucked ? null : 'full'})},
                    {key: 'untucked', label: 'Навыпуск', active: untucked,
                        onSelect: () => emit({tucking: untucked ? null : 'none'})},
                ])}
            </div>,
        );
    }

    if (capability.canRollSleeves) {
        const rolled = state.sleeveState === 'rolled';
        groups.push(
            <div key="sleeves" className="flex items-center gap-2">
                <span className={groupLabelClass}>Рукава</span>
                {segment([
                    {key: 'normal', label: 'Обычные', active: state.sleeveState === 'normal',
                        onSelect: () => emit({sleeveState: state.sleeveState === 'normal' ? null : 'normal'})},
                    {key: 'rolled', label: 'Закатанные', active: rolled,
                        onSelect: () => emit({sleeveState: rolled ? null : 'rolled'})},
                ])}
            </div>,
        );
    }

    if (capability.canCuff) {
        const cuffed = state.cuffState === 'rolled';
        groups.push(
            <div key="cuff" className="flex items-center gap-2">
                <span className={groupLabelClass}>Подвороты</span>
                {segment([
                    {key: 'cuffed', label: 'С подворотом', active: cuffed,
                        onSelect: () => emit({cuffState: cuffed ? null : 'rolled'})},
                ])}
            </div>,
        );
    }

    if (capability.canCinch) {
        const cinched = state.cinched === true;
        groups.push(
            <div key="cinch" className="flex items-center gap-2">
                <span className={groupLabelClass}>Утяжка</span>
                {segment([
                    {key: 'cinched', label: 'Утянута', active: cinched,
                        onSelect: () => emit({cinched: cinched ? null : true})},
                ])}
            </div>,
        );
    }

    if (groups.length === 0) return null;

    return <div className="space-y-1.5">{groups}</div>;
};

export default WearStateControls;
